import React, { useState, useEffect, useCallback, useRef } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box, Button, CircularProgress, Container, Card, CardContent, Dialog,
  AppBar, Toolbar, IconButton, Slide, Stack, FormControl, FormHelperText,
  MenuItem, Select, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getStateOptions, getCityOptions } from '../../utils/masterDataConfig';

// Direct Explicit Service Imports
import { brandService } from '../../services/brandService';
import { categoryService } from '../../services/categoryService';
import { subCategoryService } from '../../services/subCategoryService';
import { departmentService } from '../../services/departmentService';
import { vendorService } from '../../services/vendorService';
import { locationService } from '../../services/locationService';
import { customerService } from '../../services/customerService';
// Unified Service Mapping — aligned to actual service method signatures
const SERVICE_MAP = {
  brands: {
    getItems:   ()          => brandService.fetchBrands(),
    createItem: (data)      => brandService.create(data),
    updateItem: (id, data)  => brandService.update(id, data),
  },
  categories: {
    getItems:   ()          => categoryService.fetchCategories(),
    createItem: (data)      => categoryService.create(data),
    updateItem: (id, data)  => categoryService.update(id, data),
  },
  subcategories: {
    getItems:   ()          => subCategoryService.fetchSubCategories(),
    createItem: (data)      => subCategoryService.create(data),
    updateItem: (id, data)  => subCategoryService.update(id, data),
  },
  departments: {
    getItems:   ()          => departmentService.fetchDepartments(),
    createItem: (data)      => departmentService.create(data),
    updateItem: (id, data)  => departmentService.update(id, data),
  },
   vendors: {
    getItems:   ()          => vendorService.fetchVendors(),
    createItem: (data)      => vendorService.create(data),
    updateItem: (id, data)  => vendorService.update(id, data),
  },
  customers: {
    getItems:   ()          => customerService.fetchCustomers(),
    createItem: (data)      => customerService.create(data),
    updateItem: (id, data)  => customerService.update(id, data),
  },
  locations: {
    getItems:   ()          => locationService.fetchLocations(),
    createItem: (data)      => locationService.create(data),
    updateItem: (id, data)  => locationService.update(id, data),
  },
};

const getFieldPlaceholders = (collectionName, fieldName) => {
  const placeholders = {
    brands:        { brandId: 'e.g., IT-101, BR-001', brandName: 'e.g., Samsung, Apple, Dell, HP' },
    categories:    { categoryId: 'e.g., GN, SW, HW', categoryName: 'e.g., General, Software, Hardware' },
    subcategories: { subCategoryId: 'e.g., SUB-IT-001', subCategoryName: 'e.g., Laptop, Printer, Monitor' },
    departments:   { departmentId: 'e.g., DEPT-1001', name: 'e.g., Finance, IT, HR' },
     vendors:       { vendorId: 'e.g., VEND-001', vendorName: 'e.g., Tech Solutions Pvt Ltd' },
    customers:     { customerId: 'e.g., CUST-001', customerName: 'e.g., John Doe' },
    locations: {
      locationCode: 'e.g., LOC-001, WARE-001',
      locationName: 'e.g., Main Warehouse',
      address:      'e.g., 123 Business Park',
      city:         'e.g., Mumbai, Delhi',
      pinCode:      'e.g., 400001, 110001',
    },
    preferences: {
      preferenceName:  'e.g. Primary Asset, Owned, Monthly',
      preferenceValue: 'e.g. Primary Asset',
    },
  };
  return placeholders[collectionName]?.[fieldName] || '';
};

const getValidationMessages = (collectionName) => {
  const messages = {
    subcategories: {
      parentRequired:
        'Please select a parent category to proceed with subcategory creation.',
    },
  };
  return messages[collectionName] || {};
};

const DEPRECIATION_CHILD_FIELDS = {
  '':                   ['usefulLife', 'depreciationRate'],
  'straight-line':      ['depreciationRate'],
  'written-down-value': ['usefulLife'],
};

const COLLECTION_META = {
  brands:        { idField: 'brandId'       },
  categories:    { idField: 'categoryId'    },
  subcategories: { idField: 'subCategoryId' },
  departments:   { idField: 'departmentId'  },
  vendors:       { idField: 'vendorId'      },
   customers:     { idField: 'customerId'    },
  locations:     { idField: 'locationCode'  },
  preferences:   { idField: null            },
};

const DEPRECIATION_OPTIONAL_FIELDS = new Set([
  'depreciation', 'usefulLife', 'depreciationRate',
]);

const PROTECTED_SYSTEM_PREFERENCE_FIELDS = new Set([
  'preferenceType', 'preferenceName', 'preferenceValue',
]);

const FULL_WIDTH_FIELDS = new Set(['description', 'address']);
const COMPACT_FIELDS = new Set([
  'status', 'pincode', 'pinCode', 'zipCode', 'gstin',
  'usefulLife', 'depreciationRate', 'phone', 'contactPerson',
  'brandId', 'categoryId', 'subCategoryId', 'departmentId',
  'vendorId', 'customerId', 'locationCode',
]);

const getFieldLayoutType = (field) => {
  if (field.type === 'textarea' || FULL_WIDTH_FIELDS.has(field.name)) return 'full';
  if (COMPACT_FIELDS.has(field.name) || field.type === 'number')      return 'compact';
  return 'normal';
};

// Styled Components
const FormRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '1rem',
  marginBottom: '1rem',
  '&:last-child': { marginBottom: 0 },
  [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr' },
}));

const FormGroup = styled(Box, {
  shouldForwardProp: (p) => p !== 'hasError' && p !== 'layoutType',
})(({ hasError, layoutType }) => ({
  gridColumn:
    layoutType === 'full'    ? '1 / -1' :
    layoutType === 'compact' ? 'span 1' : 'span 2',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  '& .MuiOutlinedInput-root': {
    background: '#fff',
    '& fieldset':             { borderColor: hasError ? '#dc2626' : '#d1d5db' },
    '&:hover fieldset':       { borderColor: hasError ? '#dc2626' : '#3b82f6' },
    '&.Mui-focused fieldset': {
      borderColor: hasError ? '#dc2626' : '#3b82f6',
      boxShadow:   hasError
        ? '0 0 0 3px rgba(220,38,38,0.12)'
        : '0 0 0 3px rgba(59,130,246,0.12)',
    },
  },
  '& .MuiInputBase-input.Mui-disabled': {
    backgroundColor: '#f3f4f6',
    color: '#6b7280',
    cursor: 'not-allowed',
  },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: '#374151',
  textTransform: 'uppercase',
  letterSpacing: '0.025em',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.375rem',
}));

const RequiredMark = styled('span')(() => ({
  color: '#ef4444',
  marginLeft: '0.125rem',
}));

const OptionalMark = styled('span')(() => ({
  fontSize: '0.7rem',
  color: '#6b7280',
  fontWeight: 400,
  marginLeft: '0.25rem',
  textTransform: 'none',
}));

const LockedTag = styled('span')(() => ({
  fontSize: '0.625rem',
  fontWeight: 600,
  color: '#92400e',
  background: '#fef3c7',
  border: '1px solid #fde68a',
  padding: '0.0625rem 0.375rem',
  borderRadius: '4px',
  marginLeft: '0.375rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
}));

const ErrorMessage = styled(FormHelperText)(() => ({
  color: '#dc2626',
  fontSize: '0.75rem',
  fontWeight: 500,
  margin: 0,
}));

const FieldHint = styled(Typography)(() => ({
  fontSize: '0.75rem',
  color: '#9ca3af',
  fontStyle: 'italic',
}));

const InfoBox = styled(Box, {
  shouldForwardProp: (p) => p !== 'warning' && p !== 'success',
})(({ warning, success }) => ({
  padding: '0.75rem 1rem',
  background: success ? '#f0fdf4' : warning ? '#fffbeb' : '#eff6ff',
  border: `1px solid ${success ? '#bbf7d0' : warning ? '#fde68a' : '#bfdbfe'}`,
  borderRadius: '6px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '0.5rem',
  fontSize: '0.8125rem',
  color: success ? '#166534' : warning ? '#92400e' : '#1e40af',
}));

const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: '1px solid #e2e8f0',
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: '0.9375rem',
  fontWeight: 700,
  color: '#1e293b',
  marginBottom: '1.25rem',
  paddingBottom: '0.75rem',
  borderBottom: '1px solid #e5e7eb',
}));

const errorBannerSx = {
  backgroundColor: '#fee2e2',
  color: '#b91c1c',
  padding: '0.75rem 1rem',
  borderRadius: '6px',
  fontSize: '0.875rem',
  borderLeft: '4px solid #dc2626',
};

const helperErrorSx = {
  color: '#dc2626',
  fontSize: '0.75rem',
  fontWeight: 500,
  margin: 0,
  marginTop: '4px',
};

const helperHintSx = {
  color: '#6b7280',
  fontSize: '0.75rem',
  fontWeight: 400,
  margin: 0,
  marginTop: '4px',
};

const cancelBtnSx = {
  px: 3, py: 1,
  backgroundColor: '#f3f4f6',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontWeight: 500,
  textTransform: 'none',
  '&:hover': { backgroundColor: '#e5e7eb' },
  '&:disabled': { opacity: 0.6 },
};

const submitBtnSx = {
  px: 3, py: 1,
  backgroundColor: '#2563eb',
  color: '#fff',
  borderRadius: '6px',
  fontWeight: 600,
  textTransform: 'none',
  minWidth: '160px',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  '&:hover': { backgroundColor: '#1d4ed8' },
  '&:disabled': { backgroundColor: '#93c5fd' },
};

const getSectionConfig = (collectionName) => {
  const configs = {
    brands: [
      { title: 'Brand Information', fields: ['brandId', 'brandName', 'description', 'status'] },
    ],
    categories: [
      { title: 'Category Information', fields: ['categoryId', 'categoryName', 'description', 'status'] },
    ],
    subcategories: [
      { title: 'Sub-Category Information', fields: ['categoryId', 'subCategoryId', 'subCategoryName', 'serializationType', 'description', 'status'] },
      { title: 'Depreciation Settings', subtitle: '(Optional)', fields: ['depreciation', 'usefulLife', 'depreciationRate'] },
    ],
    departments: [
      { title: 'Department Information', fields: ['departmentId', 'name', 'description', 'status'] },
    ],
       vendors: [
      { title: 'Vendor Information', fields: ['vendorId', 'vendorName', 'contactPerson', 'email', 'phone', 'gstin', 'status'] },
      { title: 'Address & Additional Details', fields: ['address', 'country', 'state', 'pincode', 'description'] },
    ],
    customers: [
      { title: 'Customer Information', fields: ['customerId', 'customerName', 'contactPerson', 'email', 'phone', 'gstin', 'status'] },
      { title: 'Address & Additional Details', fields: ['address', 'country', 'state', 'pincode', 'description'] },
    ],
    locations: [
      { title: 'Location Information', fields: ['locationCode', 'locationName', 'description', 'status'] },
      { title: 'Address Details',      fields: ['address', 'country', 'state', 'city', 'pinCode'] },
    ],
    preferences: [
      { title: 'Preference Information', fields: ['preferenceType', 'preferenceName', 'preferenceValue', 'status'] },
    ],
  };
  return configs[collectionName] || [{ title: 'Details', fields: [] }];
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CreateEditMasterModal = ({
  open,
  onClose,
  mode = 'create',
  collectionName,
  title,
  fields,
  initialData = null,
  documentId = null,
  additionalData = {},
  onSuccess,
}) => {
  const isEditMode = mode === 'edit';
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [dynamicFields, setDynamicFields] = useState(fields);
  const [showSuccessMsg, setShowSuccessMsg] = useState(false);

  const formDataRef = useRef(formData);
  const dynamicFieldsRef = useRef(dynamicFields);
  const initializedRef = useRef(false);

  useEffect(() => { formDataRef.current = formData; }, [formData]);
  useEffect(() => { dynamicFieldsRef.current = dynamicFields; }, [dynamicFields]);

  const isSystemPreferenceProtectedField = useCallback(
    (fieldName) =>
      collectionName === 'preferences' &&
      isEditMode &&
      Boolean(initialData?.isSystem) &&
      PROTECTED_SYSTEM_PREFERENCE_FIELDS.has(fieldName),
    [collectionName, isEditMode, initialData?.isSystem],
  );

  // Scroll lock
  useEffect(() => {
    if (!open) return undefined;
    const html = document.documentElement;
    const previousOverflow = html.style.overflow;
    const previousPaddingRight = html.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    html.style.overflow = 'hidden';
    if (scrollbarWidth > 0) html.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      html.style.overflow = previousOverflow;
      html.style.paddingRight = previousPaddingRight;
    };
  }, [open]);

   const applyDynamicFields = useCallback(
    (countryCode, stateCode, sourceFields) => {
      const base = sourceFields || dynamicFieldsRef.current;
      if (collectionName === 'vendors' || collectionName === 'customers') {
        setDynamicFields(
          base.map((f) =>
            f.name === 'state'
              ? { ...f, options: getStateOptions(countryCode) }
              : f,
          ),
        );
      } else if (collectionName === 'locations') {
        setDynamicFields(
          base.map((f) => {
            if (f.name === 'state') return { ...f, options: getStateOptions(countryCode) };
            if (f.name === 'city')  return { ...f, options: getCityOptions(countryCode, stateCode) };
            return f;
          }),
        );
      }
    },
    [collectionName],
  );

  useEffect(() => {
    if (open && !initializedRef.current) {
      const initialValues = {};
      fields.forEach((field) => {
        if (isEditMode && initialData) {
          initialValues[field.name] =
            initialData[field.name] ?? field.defaultValue ?? '';
        } else {
          initialValues[field.name] =
            additionalData?.[field.name] ?? field.defaultValue ?? '';
        }
      });

      setFormData(initialValues);
      setErrors({});
      setSubmitError('');
      setShowSuccessMsg(false);

           const country = initialValues.country || 'IN';
      const stateCd = initialValues.state || '';
      if (
        collectionName === 'vendors' ||
        collectionName === 'customers' ||
        collectionName === 'locations'
      ) {
        applyDynamicFields(country, stateCd, fields);
      } else {
        setDynamicFields(fields);
      }
      initializedRef.current = true;
    }
    if (!open) initializedRef.current = false;
  }, [open, fields, collectionName, isEditMode, initialData, additionalData, applyDynamicFields]);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setSubmitError('');
      if (isSystemPreferenceProtectedField(name)) return;

      setFormData((prev) => {
        const updated = { ...prev, [name]: value };

        if (name === 'country') {
          applyDynamicFields(value, '', undefined);
          updated.state = '';
          if (collectionName === 'locations') updated.city = '';
        }
        if (name === 'state' && collectionName === 'locations') {
          applyDynamicFields(prev.country || 'IN', value, undefined);
          updated.city = '';
        }
        if (name === 'depreciation' && collectionName === 'subcategories') {
          const toClear = DEPRECIATION_CHILD_FIELDS[value] ?? [];
          toClear.forEach((f) => { updated[f] = ''; });
        }
        if (
          collectionName === 'preferences' &&
          name === 'preferenceName' &&
          !prev.preferenceValue
        ) {
          updated.preferenceValue = value;
        }
        return updated;
      });

      setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
    },
    [collectionName, applyDynamicFields, isSystemPreferenceProtectedField],
  );

  const getVisibleFields = useCallback(() => {
    if (collectionName !== 'subcategories') return dynamicFieldsRef.current;
    const method = formDataRef.current.depreciation;
    return dynamicFieldsRef.current.filter((field) => {
      if (field.name === 'usefulLife')       return method === 'straight-line';
      if (field.name === 'depreciationRate') return method === 'written-down-value';
      return true;
    });
  }, [collectionName]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    const validationMessages = getValidationMessages(collectionName);
    const fd = formDataRef.current;
    const visible = getVisibleFields();

    visible.forEach((field) => {
      const value = fd[field.name];

      if (field.required && (value === '' || value === null || value === undefined)) {
        newErrors[field.name] = `${field.label} is required`;
      }
      if (
        collectionName === 'subcategories' &&
        field.name === 'categoryId' &&
        !value
      ) {
        newErrors[field.name] =
          validationMessages.parentRequired ||
          'Please select a parent category to proceed';
      }
      if (field.pattern && value) {
        const regex = new RegExp(field.pattern);
        if (!regex.test(value)) newErrors[field.name] = field.title || 'Invalid format';
      }
      if (field.type === 'email' && value && !/^\S+@\S+\.\S+$/.test(value)) {
        newErrors[field.name] = 'Invalid email format';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [collectionName, getVisibleFields]);

  // Client-Side Duplicate-ID Checker
  const isDuplicateValue = async () => {
    const meta = COLLECTION_META[collectionName];
    if (!meta?.idField) return false;

    const targetService = SERVICE_MAP[collectionName];
    if (!targetService || !targetService.getItems) return false;

    try {
      const rawData = await targetService.getItems();
      const itemsList = Array.isArray(rawData) ? rawData : [];

      const inputIdValue = String(formData[meta.idField]).trim().toLowerCase();

      return itemsList.some((item) => {
        const existingIdValue = String(item[meta.idField] || "").trim().toLowerCase();
        const isSameId = existingIdValue === inputIdValue;
        const isDifferentDocument = item.id !== documentId;
        return isSameId && isDifferentDocument;
      });
    } catch (err) {
      console.error("[CreateEditMasterModal] Local duplicate verification failed:", err);
      return false;
    }
  };

  const handleSubmit = useCallback(
    async (e) => {
      if (e && e.preventDefault) e.preventDefault();
      if (!validateForm()) return;

      setLoading(true);
      setSubmitError('');

      const fd = formDataRef.current;
      const targetService = SERVICE_MAP[collectionName];

      try {
        const meta = COLLECTION_META[collectionName];

        if (meta?.idField && fd[meta.idField]) {
          const idChanged = !isEditMode || fd[meta.idField] !== initialData?.[meta.idField];

          if (idChanged) {
            const isDuplicate = await isDuplicateValue();

            if (isDuplicate) {
              setErrors((prev) => ({
                ...prev,
                [meta.idField]: `This ${meta.idField} already exists for your company`,
              }));
              setSubmitError(`Duplicate ${meta.idField} found. Please use a unique ID.`);
              setLoading(false);
              return;
            }
          }
        }

        let preferenceOverrides = {};
        let extras = {};
        if (collectionName === 'preferences') {
          if (isEditMode && initialData?.isSystem) {
            preferenceOverrides = {
              preferenceType:  initialData.preferenceType,
              preferenceName:  initialData.preferenceName,
              preferenceValue: initialData.preferenceValue,
            };
          }
          extras = {
            preferenceOptions: (isEditMode && initialData?.preferenceOptions) || {},
            sortOrder: (isEditMode && initialData?.sortOrder) ?? 999,
            isActive: fd.status !== 'inactive',
          };
        }

        const raw = {
          ...fd,
          ...extras,
          ...preferenceOverrides,
          ...additionalData,
        };
        const payload = Object.fromEntries(
          Object.entries(raw).map(([k, v]) => [k, v === '' ? null : v]),
        );

        if (isEditMode) {
          await targetService.updateItem(documentId, payload);
          showToast(`${title} updated successfully!`, 'success');
          onSuccess();
          onClose();
        } else {
          await targetService.createItem(payload);

          if (collectionName === 'categories') {
            showToast(
              'Category created successfully! Remember to create subcategories for better organization.',
              'success',
            );
            setShowSuccessMsg(true);
            setTimeout(() => {
              setShowSuccessMsg(false);
              onSuccess();
              onClose();
            }, 3000);
          } else {
            showToast(`${title} created successfully!`, 'success');
            onSuccess();
            onClose();
          }
        }
      } catch (err) {
        console.error(`Error ${isEditMode ? 'updating' : 'creating'} document:`, err);
        const action = isEditMode ? 'update' : 'create';
        setSubmitError(`Failed to ${action} ${title.toLowerCase()}: ${err.message}`);
        showToast(`Failed to ${action} ${title.toLowerCase()}`, 'error');
      } finally {
        setLoading(false);
      }
    },
    [
      validateForm, collectionName, isEditMode, initialData,
      documentId, additionalData, title, showToast, onSuccess, onClose,
    ],
  );

  if (!open) return null;
  if (isEditMode && !initialData) return null;

  const visibleFields = (() => {
    if (collectionName !== 'subcategories') return dynamicFields;
    const method = formData.depreciation;
    return dynamicFields.filter((field) => {
      if (field.name === 'usefulLife')       return method === 'straight-line';
      if (field.name === 'depreciationRate') return method === 'written-down-value';
      return true;
    });
  })();

  const visibleFieldMap = new Map(visibleFields.map((f) => [f.name, f]));
  const sectionConfig = getSectionConfig(collectionName);
  const modalTitle = isEditMode ? `Edit ${title}` : `Create New ${title}`;

  const getSectionFields = (sectionFieldNames) =>
    sectionFieldNames.map((name) => visibleFieldMap.get(name)).filter(Boolean);

  const renderField = (field) => {
    const fieldOptions = field.options || [];
    const placeholder = getFieldPlaceholders(collectionName, field.name);
    const hasError = Boolean(errors[field.name]);
    const layout = getFieldLayoutType(field);
    const isOptionalDep =
      collectionName === 'subcategories' &&
      DEPRECIATION_OPTIONAL_FIELDS.has(field.name) &&
      !field.required;

    const isProtected = isSystemPreferenceProtectedField(field.name);
    const isDisabled  = Boolean(field.disabled) || isProtected;

    const labelNode = (
      <FieldLabel>
        {field.label}
        {field.required && !isProtected && <RequiredMark>*</RequiredMark>}
        {isOptionalDep && <OptionalMark>(optional)</OptionalMark>}
        {isProtected && (
          <LockedTag>
            <LockOutlinedIcon sx={{ fontSize: '0.75rem' }} />
            Locked
          </LockedTag>
        )}
      </FieldLabel>
    );

    const protectedHint = 'Locked — system value used by the app. Deactivate via Status to remove from use.';
    const resolvedHelperText = hasError
      ? errors[field.name]
      : isProtected
        ? protectedHint
        : field.helpText || '';

    if (field.type === 'textarea') {
      return (
        <FormGroup key={field.name} hasError={hasError} layoutType={layout}>
          {labelNode}
          <TextField
            id={field.name}
            name={field.name}
            value={formData[field.name] || ''}
            onChange={handleChange}
            placeholder={placeholder}
            multiline minRows={2} maxRows={5}
            fullWidth size="small"
            disabled={isDisabled}
            error={hasError}
            helperText={resolvedHelperText}
            FormHelperTextProps={{ sx: hasError ? helperErrorSx : helperHintSx }}
          />
        </FormGroup>
      );
    }

    if (field.type === 'select') {
      return (
        <FormGroup key={field.name} hasError={hasError} layoutType={layout}>
          {labelNode}
          <FormControl fullWidth size="small" error={hasError} disabled={isDisabled}>
            <Select
              id={field.name}
              name={field.name}
              value={formData[field.name] || ''}
              onChange={handleChange}
              disabled={isDisabled}
              displayEmpty
            >
              <MenuItem value="" disabled>
                <Typography sx={{ opacity: 0.6, fontStyle: 'italic', fontSize: '0.875rem' }}>
                  Select {field.label}
                </Typography>
              </MenuItem>
              {fieldOptions.length === 0 ? (
                <MenuItem value="" disabled>
                  <Typography sx={{ opacity: 0.6, fontStyle: 'italic', fontSize: '0.8125rem' }}>
                    No {field.label.toLowerCase()} available
                  </Typography>
                </MenuItem>
              ) : (
                fieldOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))
              )}
            </Select>
            {hasError && <ErrorMessage>{errors[field.name]}</ErrorMessage>}
            {!hasError && isProtected && <FieldHint>{protectedHint}</FieldHint>}
            {!hasError && !isProtected && field.helpText && <FieldHint>{field.helpText}</FieldHint>}
          </FormControl>
        </FormGroup>
      );
    }

    return (
      <FormGroup key={field.name} hasError={hasError} layoutType={layout}>
        {labelNode}
        <TextField
          type={field.type || 'text'}
          id={field.name}
          name={field.name}
          value={formData[field.name] || ''}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={isDisabled}
          inputProps={{
            pattern: field.pattern,
            title: field.title,
            min: field.min,
            max: field.max,
            step: field.step,
            readOnly: isProtected,
          }}
          fullWidth size="small"
          error={hasError}
          helperText={resolvedHelperText}
          FormHelperTextProps={{ sx: hasError ? helperErrorSx : helperHintSx }}
        />
      </FormGroup>
    );
  };

  const sectionFieldNames = new Set(sectionConfig.flatMap((s) => s.fields));
  const uncategorisedFields = visibleFields.filter(
    (f) => !sectionFieldNames.has(f.name),
  );

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={!loading ? onClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
    >
      <AppBar sx={{ position: 'relative', flexShrink: 0 }} elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} disabled={loading} aria-label="Close">
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{modalTitle}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {isEditMode
                ? 'Update the details below and submit to save changes'
                : 'Fill in the details below and submit to create the record'}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f1f5f9', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box component="form" id="master-form" onSubmit={handleSubmit} noValidate
          sx={{ flex: 1, overflowY: 'auto', py: 4 }}>
          <Container maxWidth="lg">
            <Stack spacing={3}>
              {showSuccessMsg && !isEditMode && collectionName === 'categories' && (
                <InfoBox success>
                  <Box>
                    <Typography sx={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: '#166534' }}>
                      Category created successfully!
                    </Typography>
                    <Typography sx={{ margin: 0, fontSize: '0.75rem', color: '#166534', marginTop: '0.25rem' }}>
                      Next Step: Create subcategories under this category for better product organization.
                    </Typography>
                  </Box>
                </InfoBox>
              )}

              {submitError && <Box sx={errorBannerSx}>{submitError}</Box>}

              {collectionName === 'preferences' && isEditMode && initialData?.isSystem && (
                <InfoBox warning>
                  <Box>
                    <Typography sx={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <LockOutlinedIcon fontSize="small" />
                      System Preference — Core Fields Locked
                    </Typography>
                    <Typography sx={{ margin: 0, fontSize: '0.75rem', mt: 0.25 }}>
                      Preference Type, Name, and Value are read by application code and
                      cannot be renamed. To stop using this preference, toggle the Status
                      field to <strong>Inactive</strong>. This record cannot be deleted.
                    </Typography>
                  </Box>
                </InfoBox>
              )}

              {sectionConfig.map((section, sectionIdx) => {
                const sectionFields = getSectionFields(section.fields);
                if (sectionFields.length === 0) return null;

                return (
                  <SectionCard key={section.title || sectionIdx} variant="outlined">
                    <CardContent sx={{ p: 3 }}>
                      <SectionTitle>
                        {section.title}
                        {section.subtitle && (
                          <Typography component="span" sx={{
                            fontSize: '0.75rem', fontWeight: 400, color: '#6b7280',
                            fontStyle: 'italic', ml: 1,
                          }}>
                            {section.subtitle}
                          </Typography>
                        )}
                      </SectionTitle>
                      <FormRow>
                        {sectionFields.map((field) => renderField(field))}
                      </FormRow>
                    </CardContent>
                  </SectionCard>
                );
              })}

              {uncategorisedFields.length > 0 && (
                <SectionCard variant="outlined">
                  <CardContent sx={{ p: 3 }}>
                    <SectionTitle>Additional Details</SectionTitle>
                    <FormRow>
                      {uncategorisedFields.map((field) => renderField(field))}
                    </FormRow>
                  </CardContent>
                </SectionCard>
              )}
            </Stack>
          </Container>
        </Box>

        <Box sx={{
          borderTop: '1px solid #e2e8f0', backgroundColor: '#fff',
          py: 2, px: 3, boxShadow: '0 -2px 8px rgba(0,0,0,0.04)', flexShrink: 0,
        }}>
          <Container maxWidth={false} disableGutters>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Button type="button" onClick={onClose} disabled={loading} sx={cancelBtnSx}>
                Cancel
              </Button>
              <Button type="submit" form="master-form" disabled={loading} sx={submitBtnSx}>
                {loading && <CircularProgress size={16} color="inherit" />}
                {loading ? 'Processing…' : 'Submit'}
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreateEditMasterModal;